import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface SavingsData {
  postcode: string;
  housingType: string;
  currentElectricityUsage: number; // kWh/year
  currentGasUsage: number; // m³/year
  roofArea?: number; // m² for solar
  solarCapacity?: number; // kWp
  heatPumpCapacity?: number; // kW
  insulationArea?: number; // m² 
  insulationType?: string; // 'roof', 'wall', 'floor'
}

interface SavingsResult {
  measure: string;
  annualSavings: number;
  monthlySavings: number;
  energyProduction?: number; // kWh for solar
  gasSavings?: number; // m³ for heatpump/insulation
  electricitySavings?: number; // kWh
  percentage: number; // % of current usage
  co2Reduction: number; // kg CO2/year
}

interface EnergyTariffs {
  electricity_price: number;
  gas_price: number;
}

export const useSavingsCalculation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [savingsResults, setSavingsResults] = useState<SavingsResult[]>([]);
  const [energyTariffs, setEnergyTariffs] = useState<EnergyTariffs | null>(null);

  const getEnergyTariffs = async (postcode: string): Promise<EnergyTariffs> => {
    const postcodeNum = parseInt(postcode.substring(0, 4));
    
    const { data, error } = await supabase
      .from('energy_tariffs')
      .select('electricity_price, gas_price')
      .lte('postcode_range', `${Math.floor(postcodeNum / 1000)}999`)
      .gte('postcode_range', `${Math.floor(postcodeNum / 1000)}000`)
      .maybeSingle();

    if (error || !data) {
      // Fallback to national average
      return { electricity_price: 0.23, gas_price: 1.45 };
    }

    return data;
  };

  const getSavingsFactor = async (measureType: string, housingType: string) => {
    const { data, error } = await supabase
      .from('energy_savings_factors')
      .select('*')
      .eq('measure_type', measureType)
      .eq('housing_type', housingType)
      .maybeSingle();

    if (error || !data) {
      console.warn(`No savings factor found for ${measureType} - ${housingType}`);
      return null;
    }

    return data;
  };

  const calculateSolarSavings = async (data: SavingsData, tariffs: EnergyTariffs): Promise<SavingsResult | null> => {
    if (!data.solarCapacity) return null;

    const factor = await getSavingsFactor('solar', data.housingType);
    if (!factor) return null;

    const annualProduction = data.solarCapacity * factor.efficiency_factor; // kWh/year
    const selfConsumption = Math.min(annualProduction, data.currentElectricityUsage * 0.7); // 70% self-consumption
    const feedIn = annualProduction - selfConsumption;
    
    const savings = (selfConsumption * tariffs.electricity_price) + (feedIn * 0.07); // €0.07 feed-in tariff
    const percentage = (selfConsumption / data.currentElectricityUsage) * 100;
    const co2Reduction = annualProduction * 0.39; // kg CO2 per kWh

    return {
      measure: 'Zonnepanelen',
      annualSavings: Math.round(savings),
      monthlySavings: Math.round(savings / 12),
      energyProduction: Math.round(annualProduction),
      electricitySavings: Math.round(selfConsumption),
      percentage: Math.round(percentage * 10) / 10,
      co2Reduction: Math.round(co2Reduction)
    };
  };

  const calculateHeatPumpSavings = async (data: SavingsData, tariffs: EnergyTariffs): Promise<SavingsResult | null> => {
    if (!data.heatPumpCapacity || !data.currentGasUsage) return null;

    const factor = await getSavingsFactor('heatpump', data.housingType);
    if (!factor) return null;

    const gasSavings = data.currentGasUsage * 0.85; // 85% gas reduction
    const electricityIncrease = (gasSavings * 31.65) / (factor.efficiency_factor * 3.6); // kWh increase
    
    const totalSavings = (gasSavings * tariffs.gas_price) - (electricityIncrease * tariffs.electricity_price);
    const percentage = (gasSavings / data.currentGasUsage) * 100;
    const co2Reduction = (gasSavings * 1.88) - (electricityIncrease * 0.39); // kg CO2

    return {
      measure: 'Warmtepomp',
      annualSavings: Math.round(totalSavings),
      monthlySavings: Math.round(totalSavings / 12),
      gasSavings: Math.round(gasSavings),
      electricitySavings: -Math.round(electricityIncrease),
      percentage: Math.round(percentage * 10) / 10,
      co2Reduction: Math.round(co2Reduction)
    };
  };

  const calculateInsulationSavings = async (data: SavingsData, tariffs: EnergyTariffs): Promise<SavingsResult | null> => {
    if (!data.insulationArea || !data.insulationType) return null;

    const factor = await getSavingsFactor(`${data.insulationType}_insulation`, data.housingType);
    if (!factor) return null;

    const gasSavings = data.insulationArea * factor.efficiency_factor; // m³/year
    const actualSavings = Math.min(gasSavings, data.currentGasUsage * 0.4); // Max 40% reduction
    
    const totalSavings = actualSavings * tariffs.gas_price;
    const percentage = (actualSavings / data.currentGasUsage) * 100;
    const co2Reduction = actualSavings * 1.88; // kg CO2

    return {
      measure: `${data.insulationType === 'roof' ? 'Dak' : data.insulationType === 'wall' ? 'Muur' : 'Vloer'}isolatie`,
      annualSavings: Math.round(totalSavings),
      monthlySavings: Math.round(totalSavings / 12),
      gasSavings: Math.round(actualSavings),
      percentage: Math.round(percentage * 10) / 10,
      co2Reduction: Math.round(co2Reduction)
    };
  };

  const calculateAllSavings = async (data: SavingsData) => {
    setIsLoading(true);
    
    try {
      const tariffs = await getEnergyTariffs(data.postcode);
      setEnergyTariffs(tariffs);

      const results: SavingsResult[] = [];

      // Calculate solar savings
      if (data.solarCapacity) {
        const solarResult = await calculateSolarSavings(data, tariffs);
        if (solarResult) results.push(solarResult);
      }

      // Calculate heat pump savings
      if (data.heatPumpCapacity) {
        const heatPumpResult = await calculateHeatPumpSavings(data, tariffs);
        if (heatPumpResult) results.push(heatPumpResult);
      }

      // Calculate insulation savings
      if (data.insulationArea && data.insulationType) {
        const insulationResult = await calculateInsulationSavings(data, tariffs);
        if (insulationResult) results.push(insulationResult);
      }

      setSavingsResults(results);

    } catch (error) {
      console.error('Error calculating savings:', error);
      setSavingsResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    calculateAllSavings,
    isLoading,
    savingsResults,
    energyTariffs
  };
};